import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Copy, RefreshCw, Check, Info, Link2, Loader2, Mail, Smartphone, MessageSquare, AlertTriangle, Shuffle, BookOpenText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { generatePassword, entropyBits, entropyBitsForString, strength } from "../lib/passwords";
import { generateReadablePassword, readableEntropyBits, insertReadableWord, readableWordEntropyAddon, estimateReadableBits } from "../lib/readable";
import { expirationOptions, expLabel } from "../lib/expirations";
import { copyToClipboard } from "../lib/clipboard";
import { encryptSecret } from "../lib/crypto";
import { encryptForSmsDeliveryV3, formatOralCode } from "../lib/crypto";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { DirectoryPicker } from "./DirectoryPicker";

const OPTION_DEFS = [
  { key: "uppercase", labelKey: "options.uppercase", hint: "A-Z", testid: "switch-uppercase" },
  { key: "lowercase", labelKey: "options.lowercase", hint: "a-z", testid: "switch-lowercase" },
  { key: "numbers", labelKey: "options.numbers", hint: "0-9", testid: "switch-numbers" },
  { key: "symbols", labelKey: "options.symbols", hint: "!@#$%", testid: "switch-symbols" },
];

export const GeneratorCard = ({ variant = "page", initialPassword = "", initialMode = "readable" }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("generator");
  // Langue lue au moment de (re)générer : changer de langue ne transforme pas le mot de passe affiché.
  const langRef = useRef(i18n.language);
  langRef.current = i18n.language;
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
    excludeAmbiguous: true,
  });
  const [password, setPassword] = useState(initialPassword || "");
  const [mode, setMode] = useState(initialMode === "random" ? "random" : "readable");
  const [includeWord, setIncludeWord] = useState(false);
  const [autoEntropy, setAutoEntropy] = useState(null);
  const [copiedPw, setCopiedPw] = useState(false);
  const [expiration, setExpiration] = useState("1h");
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [mailtoEmail, setMailtoEmail] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [ent, setEnt] = useState(null);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [dirPickerOpen, setDirPickerOpen] = useState(false);
  const [dirPickerTarget, setDirPickerTarget] = useState("mailto");
  const [deliveryMethod, setDeliveryMethod] = useState("link");
  const [smsPhone, setSmsPhone] = useState("");
  const [smsExpiration, setSmsExpiration] = useState("1h");
  const [smsCode, setSmsCode] = useState(null);
  const [smsContact, setSmsContact] = useState(null); // contact Annuaire ayant prérempli le numéro
  const [smsInfo, setSmsInfo] = useState(null);
  const [creatingSms, setCreatingSms] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const fieldRef = useRef(null);
  const { user } = useAuth();
  const isPaid = !!user && ["pro", "team", "business", "enterprise"].includes(user.plan);
  // Durées disponibles : Anonyme (10m/1h/4h) · Free connecté (…/24h) · Payant (jusqu'à ent.max_secret_expiration).
  const expirations = expirationOptions({ user, isPaid, maxSecretExpiration: ent?.max_secret_expiration });
  const otpIncluded = !!ent?.includes_recipient_otp;
  const otpAvailable = !!ent?.can_use_recipient_otp;
  const smsIncluded = !!ent?.includes_sms_delivery;
  const smsAvailable = !!ent?.can_use_sms_delivery;

  // Garantit que la durée sélectionnée reste autorisée quand le type d'utilisateur change.
  useEffect(() => {
    if (!expirations.some((e) => e.key === expiration)) {
      const fallback = expirations.some((e) => e.key === "1h") ? "1h" : expirations[expirations.length - 1]?.key;
      if (fallback) setExpiration(fallback);
    }
  }, [expirations, expiration]);

  useEffect(() => {
    if (!user) { setEnt(null); return; }
    api.get("/account/entitlements").then(({ data }) => setEnt(data)).catch(() => setEnt(null));
  }, [user]);

  useEffect(() => {
    const el = fieldRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [password]);

  // Le lien créé devient obsolète si le mot de passe ou la durée change
  useEffect(() => {
    setCreatedLink(null);
    setCopiedLink(false);
    setSmsCode(null);
    setSmsInfo(null);
  }, [password, expiration]);

  const createSmsDelivery = async () => {
    if (!password || !smsPhone.trim()) return;
    setCreatingSms(true);
    setSmsCode(null);
    try {
      // ZK : chiffrement + code oral 9 chiffres générés localement. Le code ne quitte jamais ce navigateur.
      const enc = await encryptForSmsDeliveryV3({ password, note: null });
      const { data } = await api.post("/deliveries", {
        ...enc.payload,
        recipient_phone: smsPhone.trim(),
        expiration: smsExpiration,
      });
      setSmsCode(enc.code);
      setSmsInfo(data);
      toast.success(t("toast.smsTaken", { phone: data.phone_masked }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreatingSms(false);
    }
  };

  const copyCode = async () => {
    if (!smsCode) return;
    const ok = await copyToClipboard(formatOralCode(smsCode));
    if (!ok) { toast.error(t("toast.copyCodeFail")); return; }
    setCopiedCode(true);
    toast.success(t("toast.codeCopied"));
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const pwFontClass =
    password.length <= 24
      ? "text-xl sm:text-2xl"
      : password.length <= 40
      ? "text-lg sm:text-xl"
      : password.length <= 56
      ? "text-base sm:text-lg"
      : "text-sm sm:text-base";

  const regenerate = useCallback(() => {
    if (mode === "readable") {
      const r = generateReadablePassword(langRef.current);
      setPassword(r.password);
      setAutoEntropy(readableEntropyBits(r.wordLength, langRef.current));
    } else {
      const base = generatePassword(length, options);
      if (includeWord) {
        const r = insertReadableWord(base, langRef.current);
        setPassword(r.password);
        setAutoEntropy(Math.round(entropyBits(length, options) + readableWordEntropyAddon(r.wordLength)));
      } else {
        setPassword(base);
        setAutoEntropy(entropyBits(length, options));
      }
    }
    setCopiedPw(false);
  }, [mode, length, options, includeWord]);

  // Régénère à chaque changement de mode/longueur/options, MAIS conserve un mot de passe
  // pré-rempli à l'arrivée (page /generateur depuis l'accueil). Garde basé sur la SIGNATURE
  // des entrées (robuste au double-montage de React.StrictMode, contrairement à un flag booléen).
  const inputsKey = `${mode}|${length}|${JSON.stringify(options)}|${includeWord}`;
  const lastInputsKey = useRef(null);
  useEffect(() => {
    if (lastInputsKey.current === null) {
      lastInputsKey.current = inputsKey;
      if (initialPassword) return; // conserver le mot de passe pré-rempli
      regenerate();
      return;
    }
    if (lastInputsKey.current !== inputsKey) {
      lastInputsKey.current = inputsKey;
      regenerate();
    }
  }, [inputsKey, initialPassword, regenerate]);

  // Mode lisible : entropie du mécanisme, estimée de façon consciente de la structure (un bloc de
  // lettres vaut au plus « un mot »). Reste cohérente (~62 bits) et ne grimpe jamais faussement si
  // l'utilisateur édite/supprime un caractère. Mode aléatoire : entropie mécanique, sinon estimation.
  const bits =
    mode === "readable"
      ? estimateReadableBits(password)
      : autoEntropy != null
      ? autoEntropy
      : entropyBitsForString(password);
  const s = strength(bits);

  const toggleOption = (key) => {
    setOptions((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const setKeys = ["uppercase", "lowercase", "numbers", "symbols"];
      if (!setKeys.some((k) => next[k])) return prev;
      return next;
    });
  };

  const copyPassword = async () => {
    if (!password) return;
    const ok = await copyToClipboard(password);
    if (!ok) {
      toast.error(t("toast.pwCopyFail"));
      return;
    }
    setCopiedPw(true);
    toast.success(t("toast.pwCopied"));
    setTimeout(() => setCopiedPw(false), 2000);
  };

  const createLink = async () => {
    if (!password) return;
    setCreating(true);
    try {
      // Chiffrement ZERO-KNOWLEDGE côté navigateur. Le backend ne reçoit QUE le
      // ciphertext + IV. La clé n'est JAMAIS envoyée : elle est ajoutée dans le
      // fragment #<clé> du lien, qui n'est jamais transmis au serveur.
      const enc = await encryptSecret({ password, note: null });
      const { data } = await api.post("/secrets", {
        ciphertext: enc.ciphertext,
        iv: enc.iv,
        crypto_version: enc.crypto_version,
        expiration,
        send_email: false,
        recipient_verification: otpEnabled && otpAvailable ? "email_otp" : "none",
        recipient_email: otpEnabled && otpAvailable ? recipientEmail.trim() : null,
      });
      const fullLink = `${data.link}#${enc.key}`;
      setCreatedLink(fullLink);
      const ok = await copyToClipboard(fullLink);
      setCopiedLink(ok);
      toast.success(
        ok
          ? t("toast.linkCreatedCopied")
          : t("toast.linkCreated")
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const copyCreatedLink = async () => {
    const ok = await copyToClipboard(createdLink);
    if (!ok) {
      toast.error(t("toast.linkCopyFail"));
      return;
    }
    setCopiedLink(true);
    toast.success(t("toast.linkCopied"));
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Partage Zero-Knowledge strict : ouvre le logiciel de messagerie de l'utilisateur via un URI mailto:.
  // Le lien complet (avec #KEY) est construit et reste dans le navigateur. AUCUN appel API n'est effectué.
  const shareByEmail = () => {
    if (!createdLink) return;
    const to = ((otpEnabled && otpAvailable && recipientEmail.trim()) || mailtoEmail.trim() || "").trim();
    const subject = t("mailto.subject");
    const body = t("mailto.body", { link: createdLink });
    const uri = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = uri;
  };

  const reuseOtpEmail = otpEnabled && otpAvailable && !!recipientEmail.trim();

  const expLabelText = expLabel(expiration);

  // Page d'accueil : générateur volontairement simplifié (générer / copier / régénérer + accès au générateur complet).
  // Aucun champ email/SMS, aucun appel serveur : le mot de passe reste 100% dans le navigateur.
  if (variant === "embedded") {
    return (
      <TooltipProvider delayDuration={150}>
        <motion.div
          data-testid="generator-container"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="relative glass rounded-2xl p-6 sm:p-8"
        >
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-heading font-bold text-slate-900 text-lg">{t("embedded.title")}</span>
          </div>

          {/* Sélecteur de mode compact (accueil) */}
          <div className="grid grid-cols-2 gap-2 mb-4" data-testid="generator-mode">
            <button
              type="button"
              data-testid="mode-readable"
              onClick={() => setMode("readable")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                mode === "readable"
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <BookOpenText className="w-4 h-4" /> {t("mode.readable")}
            </button>
            <button
              type="button"
              data-testid="mode-random"
              onClick={() => setMode("random")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                mode === "random"
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Shuffle className="w-4 h-4" /> {t("mode.random")}
            </button>
          </div>

          <div className="relative rounded-xl bg-slate-50 border border-slate-200 p-5 mb-5">
            <div
              data-testid="password-display-input"
              className={`ph-no-capture w-full font-mono-token font-semibold tracking-wider text-slate-900 leading-relaxed break-all ${pwFontClass}`}
            >
              {password}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button
                data-testid="btn-copy-password"
                onClick={copyPassword}
                className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                aria-label={t("actions.copyPwAria")}
              >
                {copiedPw ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
              <motion.button
                data-testid="btn-regenerate-password"
                whileTap={{ rotate: 360 }}
                transition={{ duration: 0.4 }}
                onClick={regenerate}
                className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                aria-label={t("actions.regenAria")}
              >
                <RefreshCw className="w-4 h-4" />
              </motion.button>
            </div>
          </div>

          <div data-testid="password-strength-meter" className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">{t("strength.label")}</span>
              <span className="text-sm font-semibold" style={{ color: s.color }}>
                {t(`strength.l${s.level}`)} · {bits} bits
              </span>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-2 flex-1 rounded-full transition-all duration-300"
                  style={{ backgroundColor: i <= s.level ? s.color : "rgba(148,163,184,0.15)" }}
                />
              ))}
            </div>
          </div>

          <Button
            data-testid="btn-open-full-generator"
            onClick={() => navigate("/generateur", { state: { password, mode } })}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
          >
            <Link2 className="w-4 h-4 mr-2" /> {t("embedded.createLink")}
          </Button>
          <p className="text-[11px] text-slate-400 mt-2 text-center">
            {t("embedded.encNote")}
          </p>
        </motion.div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <motion.div
        data-testid="generator-container"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative glass rounded-2xl p-6 sm:p-8"
      >
        {variant === "page" ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">
                {t("page.eyebrow")}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
              {t("page.title")}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed mb-6 max-w-xl">
              {t("page.subtitle")}
            </p>
          </>
        ) : (
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-heading font-bold text-slate-900 text-lg">{t("page.simpleTitle")}</span>
          </div>
        )}

        {/* Sélecteur de mode de génération (page complète uniquement) */}
        <div className="mb-5" data-testid="generator-mode">
          <span className="text-sm text-slate-600 block mb-2">{t("mode.label")}</span>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="mode-readable"
              onClick={() => setMode("readable")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                mode === "readable"
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <BookOpenText className="w-4 h-4" /> {t("mode.readable")}
            </button>
            <button
              type="button"
              data-testid="mode-random"
              onClick={() => setMode("random")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                mode === "random"
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Shuffle className="w-4 h-4" /> {t("mode.random")}
            </button>
          </div>
        </div>

        {/* Password display (editable) */}
        <div className="relative rounded-xl bg-slate-50 border border-slate-200 p-5 mb-5">
          <textarea
            ref={fieldRef}
            data-testid="password-display-input"
            value={password}
            onChange={(e) => { setPassword(e.target.value.replace(/\n/g, "")); setAutoEntropy(null); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            rows={1}
            spellCheck={false}
            autoComplete="off"
            placeholder={t("field.placeholder")}
            aria-label={t("field.aria")}
            className={`ph-no-capture w-full bg-transparent outline-none resize-none overflow-hidden font-mono-token font-semibold tracking-wider text-slate-900 pr-2 leading-relaxed placeholder:text-slate-400 placeholder:font-sans placeholder:text-base placeholder:tracking-normal ${pwFontClass}`}
            style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
          />
          <p className="text-xs text-slate-500 mt-2">
            {t("field.hint")}
          </p>

          {/* Méthode de remise */}
          {smsIncluded && (
            <div className="mt-4" data-testid="delivery-method">
              <span className="text-sm text-slate-600 block mb-2">{t("delivery.label")}</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="method-link"
                  onClick={() => setDeliveryMethod("link")}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    deliveryMethod === "link"
                      ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Link2 className="w-4 h-4" /> {t("delivery.linkEmail")}
                </button>
                <button
                  type="button"
                  data-testid="method-sms"
                  onClick={() => setDeliveryMethod("sms")}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    deliveryMethod === "sms"
                      ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Smartphone className="w-4 h-4" /> {t("delivery.sms")}
                </button>
              </div>
            </div>
          )}

          {/* Link validity selector (méthode Lien / E-mail) */}
          {deliveryMethod === "link" && (
            <>
              <div className="flex items-center justify-between gap-3 mt-4">
                <span className="text-sm text-slate-600">{t("link.validity")}</span>
                <Select value={expiration} onValueChange={setExpiration}>
                  <SelectTrigger
                    data-testid="select-link-expiration"
                    className="h-9 w-36 bg-white border-slate-200"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expirations.map((e) => (
                      <SelectItem key={e.key} value={e.key}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isPaid && (
                <p className="text-[11px] text-slate-400 mt-1.5 text-right">
                  {t("link.extendedNote")}
                </p>
              )}
            </>
          )}

          {/* Config SMS sécurisé */}
          {deliveryMethod === "sms" && (
            <div className="mt-4 space-y-3" data-testid="sms-config">
              {!smsAvailable && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  {t("sms.notActive")}
                </div>
              )}
              <div>
                <label className="text-sm text-slate-600 block mb-1.5">{t("sms.phoneLabel")}</label>
                <input
                  data-testid="sms-phone-input"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="+687 75 12 34"
                  className="w-full rounded-md bg-white border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                />
                {smsContact?.phone_verified && smsContact.phone === smsPhone && (
                  <p data-testid="sms-phone-verified-badge" className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> {t("sms.phoneVerified")}
                  </p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">
                  {t("sms.phoneHint")}
                </p>
                {user && (
                  <button
                    type="button"
                    data-testid="pick-from-directory-sms-btn"
                    onClick={() => { setDirPickerTarget("sms"); setDirPickerOpen(true); }}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-500"
                  >
                    <BookOpenText className="w-3.5 h-3.5" /> {t("sms.pickDirectory")}
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-600">{t("sms.validity")}</span>
                <Select value={smsExpiration} onValueChange={setSmsExpiration}>
                  <SelectTrigger data-testid="select-sms-expiration" className="h-9 w-36 bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10m">{t("sms.min10")}</SelectItem>
                    <SelectItem value="1h">{t("sms.hour1")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Primary action */}
          <div className="flex items-center gap-2 mt-4">
            {deliveryMethod === "sms" ? (
              <Button
                data-testid="btn-create-sms"
                onClick={createSmsDelivery}
                disabled={!password || !smsPhone.trim() || !smsAvailable || creatingSms}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold flex-1"
              >
                {creatingSms ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                {t("actions.sendSms")}
              </Button>
            ) : (
              <Button
                data-testid="btn-create-link"
                onClick={createLink}
                disabled={!password || creating}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold flex-1"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                {t("actions.createLink")}
              </Button>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="btn-copy-password"
                  onClick={copyPassword}
                  className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                  aria-label={t("actions.copyPwAria")}
                >
                  {copiedPw ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>{t("actions.copyPwTip")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  data-testid="btn-regenerate-password"
                  whileTap={{ rotate: 360 }}
                  transition={{ duration: 0.4 }}
                  onClick={regenerate}
                  className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                  aria-label={t("actions.regenAria")}
                >
                  <RefreshCw className="w-4 h-4" />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>{t("actions.regenTip")}</TooltipContent>
            </Tooltip>
          </div>

          {/* SMS : code oral 9 chiffres (jamais envoyé au backend ni par SMS) */}
          {smsInfo && smsCode && (
            <div data-testid="sms-unlock-box" className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-4">
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold mb-1">
                <Check className="w-4 h-4" /> {t("smsBox.taken", { phone: smsInfo.phone_masked })}
              </div>
              <p className="text-xs text-slate-600 mb-3">{t("smsBox.unlockDesc")}</p>
              <div
                data-testid="sms-oral-code"
                className="ph-no-capture select-all rounded-xl bg-white border-2 border-slate-200 py-4 text-center font-mono-token text-3xl sm:text-4xl font-bold tracking-[0.18em] text-slate-900 tabular-nums mb-3"
              >
                {formatOralCode(smsCode)}
              </div>
              <Button data-testid="btn-copy-code" onClick={copyCode} className="w-full bg-white hover:bg-slate-100 border border-slate-200 text-slate-900">
                {copiedCode ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} {t("smsBox.copyCode")}
              </Button>
              <p className="text-[11px] text-slate-600 mt-3">{t("smsBox.channel")}</p>
              <p data-testid="sms-code-warning" className="text-[11px] text-amber-600 mt-2 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {t("smsBox.warnLost")}
              </p>
            </div>
          )}

          {/* Created link display (méthode Lien / E-mail) */}
          {deliveryMethod === "link" && createdLink && (
            <div
              data-testid="created-link-box"
              className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-4"
            >
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold mb-2">
                <Check className="w-4 h-4" />
                {t("createdLink.createdValid", { dur: expLabelText })}
              </div>
              <div
                data-testid="created-link-display"
                className="ph-no-capture font-mono-token text-xs break-all text-slate-600 bg-white rounded-lg p-3 border border-slate-200 mb-2"
              >
                {createdLink}
              </div>
              <p className="text-[11px] text-slate-500 mb-3">
                {t("createdLink.keyNote")}
              </p>
              <Button
                data-testid="btn-copy-created-link"
                onClick={copyCreatedLink}
                className="w-full bg-white hover:bg-slate-100 border border-slate-200 text-slate-900"
              >
                {copiedLink ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedLink ? t("createdLink.copied") : t("createdLink.copy")}
              </Button>

              {/* Adresse e-mail facultative — strictement locale (jamais envoyée à SecurePass) */}
              {!reuseOtpEmail && (
                <div className="mt-3">
                  <input
                    data-testid="mailto-email-input"
                    type="email"
                    value={mailtoEmail}
                    onChange={(e) => setMailtoEmail(e.target.value)}
                    placeholder={t("mailto.placeholder")}
                    className="w-full rounded-md bg-white border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    {t("mailto.hint")}
                  </p>
                  {user && (
                    <button
                      type="button"
                      data-testid="pick-from-directory-mailto-btn"
                      onClick={() => { setDirPickerTarget("mailto"); setDirPickerOpen(true); }}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-500"
                    >
                      <BookOpenText className="w-3.5 h-3.5" /> Choisir dans l'annuaire
                    </button>
                  )}
                </div>
              )}

              <Button
                data-testid="btn-share-mailto"
                onClick={shareByEmail}
                className="w-full mt-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t("mailto.send")}
              </Button>
            </div>
          )}
        </div>

        {/* Strength meter */}
        <div data-testid="password-strength-meter">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">{t("strength.label")}</span>
            <span className="text-sm font-semibold" style={{ color: s.color }}>
              {t(`strength.l${s.level}`)} · {bits} bits
            </span>
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-2 flex-1 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: i <= s.level ? s.color : "rgba(148,163,184,0.15)",
                  boxShadow: i <= s.level ? `0 0 12px ${s.color}66` : "none",
                }}
              />
            ))}
          </div>
        </div>

        {/* Options de génération — masquées en mode « Sécurisé lisible » */}
        {mode === "random" ? (
          <div className="pt-4">
            {/* Character options 2x2 */}
            <div className="grid grid-cols-2 gap-3">
              {OPTION_DEFS.map((opt) => (
                <div
                  key={opt.key}
                  className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">{t(opt.labelKey)}</div>
                    <div className="text-xs text-slate-500 font-mono-token">{opt.hint}</div>
                  </div>
                  <Switch
                    data-testid={opt.testid}
                    checked={options[opt.key]}
                    onCheckedChange={() => toggleOption(opt.key)}
                  />
                </div>
              ))}
            </div>

            {/* Ambiguous (left) + Length (right) on the same row */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="flex items-center justify-between rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 px-4 py-2.5">
                <div>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                    {t("options.excludeAmbiguous")}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-slate-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {t("options.ambiguousTip")}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="text-xs text-slate-500 font-mono-token">I l 1 O 0 | ` ' "</div>
                </div>
                <Switch
                  data-testid="switch-exclude-ambiguous"
                  checked={options.excludeAmbiguous}
                  onCheckedChange={() =>
                    setOptions((p) => ({ ...p, excludeAmbiguous: !p.excludeAmbiguous }))
                  }
                />
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900">{t("options.length")}</span>
                  <span className="font-mono-token text-base font-semibold text-emerald-600">
                    {length}
                  </span>
                </div>
                <Slider
                  data-testid="slider-password-length"
                  value={[length]}
                  min={8}
                  max={64}
                  step={1}
                  onValueChange={(v) => setLength(v[0])}
                />
              </div>
            </div>

            {/* Inclure un mot lisible — AJOUTE un mot sans réduire la partie aléatoire (spec §10) */}
            <div
              data-testid="include-readable-word"
              className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5"
            >
              <div className="min-w-0 pr-3">
                <div className="text-sm font-medium text-slate-900">{t("options.includeWord")}</div>
                <div className="text-xs text-slate-500 leading-snug">{t("options.includeWordHint")}</div>
              </div>
              <Switch
                data-testid="switch-include-word"
                checked={includeWord}
                onCheckedChange={() => setIncludeWord((v) => !v)}
              />
            </div>
          </div>
        ) : (
          <div className="pt-4">
            <div
              data-testid="readable-explanation"
              className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 px-4 py-3 text-sm text-slate-600 leading-relaxed"
            >
              {t("readable.explanation")}
            </div>
          </div>
        )}

        {/* Vérification du destinataire — disponible dans les deux modes */}
        {otpIncluded && (
          <div className="pt-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3" data-testid="recipient-otp-option">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{t("otp.title")}</div>
                  <div className="text-xs text-slate-500">
                    {otpAvailable
                      ? t("otp.available")
                      : t("otp.soon")}
                  </div>
                </div>
                <Switch
                  data-testid="toggle-recipient-otp"
                  disabled={!otpAvailable}
                  checked={otpEnabled && otpAvailable}
                  onCheckedChange={(v) => setOtpEnabled(v)}
                />
              </div>
              {otpEnabled && otpAvailable && (
                <div className="mt-3">
                  <input
                    data-testid="recipient-otp-email"
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder={t("otp.emailPlaceholder")}
                    className="w-full rounded-md bg-white border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  />
                  {user && (
                    <button
                      type="button"
                      data-testid="pick-from-directory-btn"
                      onClick={() => { setDirPickerTarget("otp"); setDirPickerOpen(true); }}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-500"
                    >
                      <BookOpenText className="w-3.5 h-3.5" /> Choisir dans l'annuaire
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
      <DirectoryPicker
        open={dirPickerOpen}
        onOpenChange={setDirPickerOpen}
        channel={dirPickerTarget === "sms" ? "phone" : "email"}
        onSelect={(contact) => {
          if (dirPickerTarget === "sms") { setSmsPhone(contact.phone); setSmsContact(contact); }
          else if (dirPickerTarget === "otp") setRecipientEmail(contact.email);
          else setMailtoEmail(contact.email);
        }}
      />
    </TooltipProvider>
  );
};
